/**
 * screens/products/voice.tsx — Suki Voice product surface (UI shell, mock data).
 * Faithful RN port of design/products.jsx VoicePage: stat strip, call log table
 * (direction icon, Suki AI chip, status), and the transcript modal with AI
 * summary + download/copy actions.
 */

import React, { useState } from 'react';
import { View, Text, ScrollView } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { ProductHeader, StatStrip, PButton, PTextInput, PModal, PEmpty } from '../../components/platform/controls';
import { DataTable, Tag, StatusBadge } from '../../components/platform/primitives';
import Icon from '../../components/icon';
import { FS, FW, R } from '../../utils/platformTokens';

type Call = typeof import('../../data/platformSeed').SEED.voiceCalls[number];

const fmt = (s: number) => (s === 0 ? '—' : `${Math.floor(s / 60)}m ${s % 60}s`);

const VoiceScreen: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state } = usePlatform();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState<Call | null>(null);

  const calls = state.voiceCalls.filter((x) => `${x.contact} ${x.id} ${x.agent}`.toLowerCase().includes(q.toLowerCase()));
  const usage = state.usage.find((u) => u.label.includes('Voice'));
  const completed = state.voiceCalls.filter((x) => x.status === 'completed').length;
  const missed = state.voiceCalls.filter((x) => x.status === 'missed').length;

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1100, width: '100%', alignSelf: 'center' }}>
      <ProductHeader title="Suki Voice" sub="AI-assisted call routing, recordings and transcripts."
        action={<PButton kind="accent" icon="phone" onPress={() => toast.info('Dialer opens here in the full product.')}>New call</PButton>} />
      <StatStrip items={[
        { label: 'Calls today', value: state.voiceCalls.length },
        { label: 'Completed', value: completed, tone: c.success },
        { label: 'Missed', value: missed, tone: missed ? c.danger : c.ink },
        { label: 'Voice minutes', value: usage ? `${usage.used}/${usage.cap}` : '—', tone: c.accent },
      ]} />

      <View style={{ maxWidth: 320, marginBottom: 14 }}>
        <PTextInput leadingIcon="search" placeholder="Search calls…" value={q} onChangeText={setQ} />
      </View>

      <View style={{ borderWidth: 1, borderColor: c.line, borderRadius: R.r3, backgroundColor: c.card, overflow: 'hidden' }}>
        <DataTable<Call>
          minWidth={900}
          keyExtractor={(x) => x.id}
          rows={calls}
          empty={<PEmpty icon="phone" title="No calls found">Adjust your search.</PEmpty>}
          columns={[
            {
              key: 'contact', header: 'Contact', width: 180,
              render: (x) => (
                <View>
                  <Text style={{ fontWeight: FW.medium, color: c.ink }}>{x.contact}</Text>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: FS.sm, color: c.ink3 }}>{x.id}</Text>
                </View>
              ),
            },
            {
              key: 'direction', header: 'Direction', width: 120,
              render: (x) => (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Icon name={x.direction === 'inbound' ? 'download' : 'upload'} size={14} color={c.ink3} />
                  <Text style={{ color: c.ink2, fontSize: FS.md, textTransform: 'capitalize' }}>{x.direction}</Text>
                </View>
              ),
            },
            { key: 'duration', header: 'Duration', width: 100, render: (x) => <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>{fmt(x.duration)}</Text> },
            { key: 'agent', header: 'Handled by', width: 140, render: (x) => (x.agent === 'Suki AI' ? <Tag tone="accent" dot>Suki AI</Tag> : <Text style={{ color: c.ink2, fontSize: FS.md }}>{x.agent}</Text>) },
            { key: 'time', header: 'When', width: 150, render: (x) => <Text style={{ color: c.ink3, fontSize: FS.md }}>{x.time}</Text> },
            { key: 'status', header: 'Status', width: 110, render: (x) => <StatusBadge status={x.status} /> },
            {
              key: 'actions', header: 'Actions', align: 'right',
              render: (x) => <PButton size="sm" kind="ghost" disabled={x.status === 'missed'} onPress={() => setDetail(x)}>Transcript</PButton>,
            },
          ]}
        />
      </View>

      <PModal
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? `Call ${detail.id} · ${detail.contact}` : ''}
        footer={<PButton kind="accent" onPress={() => setDetail(null)}>Close</PButton>}
      >
        {detail && (
          <View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginBottom: 14 }}>
              <Text style={{ color: c.ink3, fontSize: FS.sm }}>Direction <Text style={{ color: c.ink, textTransform: 'capitalize' }}>{detail.direction}</Text></Text>
              <Text style={{ color: c.ink3, fontSize: FS.sm }}>Duration <Text style={{ color: c.ink, fontFamily: theme.font.mono }}>{fmt(detail.duration)}</Text></Text>
              <Text style={{ color: c.ink3, fontSize: FS.sm }}>Handled by <Text style={{ color: c.ink }}>{detail.agent}</Text></Text>
            </View>
            <Text style={{ fontSize: FS.sm, fontWeight: FW.semibold, color: c.ink2, marginBottom: 6 }}>AI summary</Text>
            <View style={{ padding: 14, backgroundColor: c.paper2, borderWidth: 1, borderColor: c.line, borderRadius: R.r3 }}>
              <Text style={{ color: c.ink2, fontSize: FS.md }}>{detail.summary}</Text>
            </View>
            <View style={{ marginTop: 14, flexDirection: 'row', gap: 8 }}>
              <PButton size="sm" icon="download" onPress={() => toast.success('Recording downloaded.')}>Download recording</PButton>
              <PButton size="sm" kind="ghost" onPress={() => toast.success('Full transcript copied.')}>Copy transcript</PButton>
            </View>
          </View>
        )}
      </PModal>
    </ScrollView>
  );
};

export default VoiceScreen;
